import { useState } from 'react';
import { useFiscalYearLocks, useUpdateDeclarationStatus } from '../hooks/useFiscalYearLocks';

async function downloadFile(path: string, filename: string, method: 'GET' | 'POST' = 'GET') {
  const res = await fetch(path, { method, credentials: 'include' });
  if (!res.ok) {
    alert(`ダウンロード失敗: ${res.status}`);
    return false;
  }
  const blob = await res.blob();
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}

export default function EtaxPage() {
  const currentYear = new Date().getFullYear();
  const [fiscalYear, setFiscalYear] = useState(currentYear - 1);
  const { data: locks } = useFiscalYearLocks();
  const updateStatusMut = useUpdateDeclarationStatus();

  const lock = locks?.find((l) => l.fiscalYear === fiscalYear);
  const isLocked = !!lock;

  const onZipDownload = async () => {
    if (!isLocked) {
      if (!confirm('この年度はまだ締め処理されていません。仮データでZIP出力しますか？\n（正式な申告データには「年度締め」の実施を推奨）')) {
        return;
      }
    }
    const ok = await downloadFile(`/api/etax/${fiscalYear}/zip`, `etax_${fiscalYear}.zip`, 'POST');
    if (ok && isLocked && lock?.declarationStatus === 'not_filed') {
      try {
        await updateStatusMut.mutateAsync({
          fiscalYear,
          declarationStatus: 'csv_exported',
        });
      } catch {
        // ステータス更新失敗は無視（CSV出力自体は成功）
      }
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>e-Tax 連携（CSV出力）</h2>
      </div>

      <div className="card">
        <p className="text-muted" style={{ marginTop: 0, fontSize: 13 }}>
          国税庁「確定申告書等作成コーナー」へ手動転記する際の参照CSVを生成します。
          ZIP ダウンロード時は R2 へも控えを保管します。
        </p>
        <div className="toolbar">
          <label>
            事業年度{' '}
            <input
              type="number"
              value={fiscalYear}
              onChange={(e) => setFiscalYear(Number(e.target.value))}
              style={{ width: 100 }}
            />
          </label>
          {isLocked ? (
            <span className="badge active">締め処理済</span>
          ) : (
            <span className="badge draft">未締め</span>
          )}
        </div>
      </div>

      <div className="card">
        <h3>個別CSVダウンロード</h3>
        <div className="toolbar">
          <button
            onClick={() =>
              downloadFile(`/api/etax/${fiscalYear}/income.csv`, `etax_${fiscalYear}_income.csv`)
            }
          >
            青色申告決算書（損益）
          </button>
          <button
            onClick={() =>
              downloadFile(`/api/etax/${fiscalYear}/balance.csv`, `etax_${fiscalYear}_balance.csv`)
            }
          >
            青色申告決算書（貸借対照表）
          </button>
          <button
            onClick={() =>
              downloadFile(
                `/api/etax/${fiscalYear}/consumption.csv`,
                `etax_${fiscalYear}_consumption.csv`,
              )
            }
          >
            消費税申告書
          </button>
        </div>
      </div>

      <div className="card">
        <h3>一括ZIPダウンロード（推奨）</h3>
        <p className="text-muted" style={{ marginTop: 0, fontSize: 13 }}>
          上記3つのCSVをZIPでまとめてダウンロードします。同時にR2へ保管し、申告状況を「CSV出力済」に更新します。
        </p>
        <div className="toolbar">
          <button className="primary" onClick={onZipDownload}>
            ZIP一括ダウンロード
          </button>
        </div>
      </div>

      <div className="card">
        <h3>申告ワークフロー</h3>
        <ol style={{ lineHeight: 1.8 }}>
          <li>「年度締め」画面で対象年度を締め処理</li>
          <li>このページで CSV を ZIP ダウンロード</li>
          <li>
            国税庁{' '}
            <a
              href="https://www.keisan.nta.go.jp/kyoutu/ky/sm/top"
              target="_blank"
              rel="noreferrer"
            >
              確定申告書等作成コーナー
            </a>{' '}
            で内容を入力（CSV値を参照）
          </li>
          <li>マイナンバーカードで電子署名し e-Tax 送信</li>
          <li>「年度締め」画面で「申告完了」を記録</li>
        </ol>
      </div>
    </div>
  );
}
